import { HttpErrorResponse } from '@angular/common/http';

/**
 * Message lisible d'une erreur d'appel au backend.
 *
 * Une `HttpErrorResponse` d'Angular n'est PAS une `Error` : `e instanceof Error ? e.message : …`
 * tombe toujours sur le texte générique et perd le message que le serveur a pris la peine
 * d'écrire. Et son propre `message` (« Http failure response for <url>: 500 … ») est technique :
 * on ne l'affiche jamais.
 *
 * Corps d'erreur réellement renvoyés (origin/develop, relevé du 2026-10-05) :
 * - sitemanager, trackermanager, gatewaymanager — `GlobalExceptionHandler` →
 *   `ErrorResponse { code, message, timestamp, path }` (ex. 409 `LORA_CAPACITY_EXCEEDED`,
 *   `LORA_PLAN_STALE`, `TRACKER_ON_OFFLINE_SITE`, `DEVICE_ALREADY_ON_ANOTHER_SITE`) ;
 * - trackermanager `TrackerSiteController` (assignation au site) →
 *   `TrackerSiteAssignmentResponse { trackerId, site: null, message }` (409 slots LoRa pleins,
 *   403 « Organization mismatch… ») : pas de `code` ;
 * - usermanager `GlobalExceptionHandler` → `{ error: <CODE>, message, …contexte }` : ici `error`
 *   est un CODE (`ORGANIZATION_NAME_TAKEN`), pas un message ;
 * - `ResponseStatusException` / erreurs non gérées → corps Spring Boot par défaut
 *   `{ timestamp, status, error: <phrase HTTP>, path }` (`include-message: never`) : `error` est
 *   « Internal Server Error », rien de plus utile que le fallback de l'appelant ;
 * - problem+json (RFC 9457) → `{ type, title, status, detail }` ;
 * - proxy / ingress (502, 504) → page HTML : jamais affichée.
 *
 * Ordre de lecture : `error.message`, `error.detail`, `error.error` s'il s'agit d'une phrase (ni un
 * code, ni la phrase HTTP d'un corps Spring par défaut), corps texte brut, `Error.message`.
 * Statut 0 (réseau coupé, CORS, serveur arrêté) → `options.unreachable`, sinon
 * {@link SERVER_UNREACHABLE_MESSAGE}.
 * Sinon `fallback`.
 */
export function errorMessage(e: unknown, fallback: string, options: ErrorMessageOptions = {}): string {
  const http = asHttpError(e);
  if (http) {
    if (http.status === 0) return options.unreachable ?? SERVER_UNREACHABLE_MESSAGE;
    return messageFromBody(http.error) ?? fallback;
  }
  if (e instanceof Error) return readable(e.message) ?? fallback;
  return fallback;
}

/**
 * Code métier d'une erreur backend (`ErrorResponse.code`, ou `error` usermanager quand c'est un
 * code), sinon `null`. Sert à distinguer deux refus de même statut (ex. 409 `LORA_PLAN_STALE` vs
 * 409 `LORA_CAPACITY_EXCEEDED`).
 */
export function errorCode(e: unknown): string | null {
  const body = asRecord(asHttpError(e)?.error);
  if (!body) return null;
  if (isCode(body['code'])) return body['code'];
  if (isCode(body['error'])) return body['error'];
  return null;
}

export const SERVER_UNREACHABLE_MESSAGE = 'Serveur injoignable';

function messageFromBody(body: unknown): string | null {
  if (typeof body === 'string') return readable(body);
  const record = asRecord(body);
  if (!record) return null;
  const fromMessage = readableField(record['message']) ?? readableField(record['detail']);
  if (fromMessage) return fromMessage;
  // Corps Spring par défaut : `error` y est la phrase HTTP (« Bad Request »), pas une explication.
  const springDefault = typeof record['status'] === 'number';
  const error = record['error'];
  if (!springDefault && typeof error === 'string' && !isCode(error)) return readable(error);
  return null;
}

function readableField(value: unknown): string | null {
  return typeof value === 'string' ? readable(value) : null;
}

/** Texte affichable tel quel : non vide, pas de HTML, pas de stacktrace. */
function readable(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('<') || /<\/?(html|body|head)\b/i.test(trimmed)) return null;
  if (/\n\s*at\s/.test(trimmed)) return null;
  return trimmed;
}

function isCode(value: unknown): value is string {
  return typeof value === 'string' && /^[A-Z][A-Z0-9]*(_[A-Z0-9]+)+$|^[A-Z]{2,}$/.test(value);
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

export interface ErrorMessageOptions {
  /** Libellé du statut 0 (défaut : {@link SERVER_UNREACHABLE_MESSAGE}) — l'integrator passe sa traduction. */
  unreachable?: string;
}

/**
 * `HttpErrorResponse`, ou objet de même forme `{ status: number, error }` (erreur relancée telle
 * quelle, simulée en test) : on lit son corps de la même façon.
 */
function asHttpError(e: unknown): { status: number; error: unknown } | null {
  if (e instanceof HttpErrorResponse) return e;
  const record = asRecord(e);
  if (record && typeof record['status'] === 'number' && 'error' in record) {
    return { status: record['status'], error: record['error'] };
  }
  return null;
}
